import moment from 'moment';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAxios } from '../../utils/hook/useAxios';
import PhotosGallery from '../PhotosGallery';

function ResaAdminOwner({ reservation, onReservationUpdated }) {
  const [showPhotos, setShowPhotos] = useState(false);
  const [showCompare, setShowCompare] = useState(false);

  const [checkinData, setCheckinData] = useState({});
  const [checkoutData, setCheckoutData] = useState({});
  const [extraPreview, setExtraPreview] = useState(null);
  const [showCheckoutConfirm, setShowCheckoutConfirm] = useState(false);
  const [lastExtraCharge, setLastExtraCharge] = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);

  const axios = useAxios();

  useEffect(() => {
    axios
      .get('/checkin/' + reservation.reservationId)
      .then((r) => r.data && setCheckinData(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios
      .get('/checkout/' + reservation.reservationId)
      .then((r) => r.data && setCheckoutData(r.data))
      .catch(() => {});
  }, []);

  // Bilan km dès que les deux relevés sont disponibles
  useEffect(() => {
    if (!checkoutData?.kmEnd) return;
    axios
      .get('/checkout/' + reservation.reservationId + '/extra-distance-preview')
      .then((r) => setExtraPreview(r.data))
      .catch(() => {});
  }, [checkoutData?.kmEnd]);

  const printInvoice = () => {
    const resa = reservation;
    const days = Math.max(
      1,
      Math.ceil((new Date(resa.endDate) - new Date(resa.startDate)) / (1000 * 60 * 60 * 24))
    );
    const pricePerDay = parseFloat(resa.VehiculeAnnonce?.reservationPrice) || 0;
    const baseTotal = days * pricePerDay;
    const insurance = parseFloat(resa.insuranceFee) || 0;
    const extraKm = extraPreview?.hasExtra ? parseFloat(extraPreview.extraAmount) || 0 : 0;
    const grandTotal = baseTotal + insurance + extraKm;

    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8"/>
<title>Facture #${resa.reservationId}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 13px; color: #222; margin: 0; padding: 32px; }
  h1 { font-size: 22px; color: #ff6600; margin-bottom: 4px; }
  .subtitle { color: #888; font-size: 12px; margin-bottom: 24px; }
  .section { margin-bottom: 20px; }
  .label { color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 4px; }
  .value { font-weight: bold; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th { background: #ff6600; color: #fff; padding: 8px 10px; text-align: left; font-size: 12px; }
  td { padding: 8px 10px; border-bottom: 1px solid #eee; font-size: 13px; }
  .total-row td { font-weight: bold; background: #fff8f3; font-size: 14px; }
  .row2 { display: flex; gap: 32px; margin-bottom: 20px; }
  .col { flex: 1; }
  .badge { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: 11px; font-weight: bold; background: #28a745; color: #fff; }
  footer { margin-top: 40px; color: #aaa; font-size: 11px; text-align: center; border-top: 1px solid #eee; padding-top: 12px; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>
<h1>Moovicar</h1>
<p class="subtitle">FACTURE DE LOCATION — Réservation #${resa.reservationId}</p>

<div class="row2">
  <div class="col">
    <div class="label">Conducteur</div>
    <div class="value">${resa.Invite?.firstName ?? ''} ${resa.Invite?.lastName ?? ''}</div>
    <div>${resa.Invite?.city ?? ''} ${resa.Invite?.country ?? ''}</div>
  </div>
  <div class="col">
    <div class="label">Hôte</div>
    <div class="value">${resa.Host?.firstName ?? ''} ${resa.Host?.lastName ?? ''}</div>
    <div>${resa.Host?.city ?? ''} ${resa.Host?.country ?? ''}</div>
  </div>
  <div class="col">
    <div class="label">Véhicule</div>
    <div class="value">${resa.Vehicle?.VehicleModel?.marque ?? ''} ${resa.Vehicle?.VehicleModel?.modele ?? ''}</div>
  </div>
</div>

<div class="row2">
  <div class="col">
    <div class="label">Période de location</div>
    <div class="value">${moment(resa.startDate).format('DD/MM/YYYY HH:mm')} → ${moment(resa.endDate).format('DD/MM/YYYY HH:mm')}</div>
    <div>${days} jour${days > 1 ? 's' : ''}</div>
  </div>
  <div class="col">
    <div class="label">Emplacement de départ</div>
    <div>${resa.VehiculeAnnonce?.locationAddress ?? '—'}</div>
  </div>
  <div class="col">
    <div class="label">Statut</div>
    <span class="badge">${resa.status}</span>
  </div>
</div>

<table>
  <thead>
    <tr>
      <th>Description</th>
      <th style="text-align:right">Quantité</th>
      <th style="text-align:right">Prix unitaire</th>
      <th style="text-align:right">Montant</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Location ${resa.Vehicle?.VehicleModel?.marque ?? ''} ${resa.Vehicle?.VehicleModel?.modele ?? ''}</td>
      <td style="text-align:right">${days} j.</td>
      <td style="text-align:right">${pricePerDay.toFixed(2)} €</td>
      <td style="text-align:right">${baseTotal.toFixed(2)} €</td>
    </tr>
    ${
      insurance > 0
        ? `<tr>
      <td>Assurance</td>
      <td style="text-align:right">1</td>
      <td style="text-align:right">${insurance.toFixed(2)} €</td>
      <td style="text-align:right">${insurance.toFixed(2)} €</td>
    </tr>`
        : ''
    }
    ${
      extraKm > 0
        ? `<tr>
      <td>Km supplémentaires (${extraPreview.kmExcess} km × ${extraPreview.pricePerKm} €/km)</td>
      <td style="text-align:right">1</td>
      <td style="text-align:right">${extraKm.toFixed(2)} €</td>
      <td style="text-align:right">${extraKm.toFixed(2)} €</td>
    </tr>`
        : ''
    }
    <tr class="total-row">
      <td colspan="3">Total TTC</td>
      <td style="text-align:right">${grandTotal.toFixed(2)} €</td>
    </tr>
  </tbody>
</table>

<footer>Moovicar — Document généré le ${moment().format('DD/MM/YYYY à HH:mm')} — Réservation #${resa.reservationId}</footer>
<script>window.onload = function(){ window.print(); }</script>
</body>
</html>`;

    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  // Affiche le badge si un paiement extra a déjà été effectué
  const extraAlreadyPaid =
    reservation.extraDistancePaid === true || reservation.extraDistancePaid === 1;
  const extraFailed =
    reservation.distanceParcourueKm > reservation.distanceMaxKm && !extraAlreadyPaid;

  const handleValidateCheckin = async () => {
    try {
      const { data } = await axios.put('/checkin/' + reservation.reservationId + '/validate');
      if (data) {
        toast.success('Validation effectuée avec succès');
        onReservationUpdated?.();
      }
    } catch (error) {
      console.error('Erreur check-in :', error.response?.data || error.message);
      toast.error('Erreur lors de la validation du check-in');
    }
  };

  const handleValidateCheckout = async () => {
    setShowCheckoutConfirm(false);
    try {
      const { data } = await axios.put('/checkout/' + reservation.reservationId + '/validate');
      if (data) {
        if (data.extraCharge?.charged) {
          setLastExtraCharge(data.extraCharge);
          toast.success(
            `Checkout validé — ${data.extraCharge.amount.toFixed(2)} € prélevés (km supplémentaires)`
          );
        } else if (data.extraCharge && !data.extraCharge.charged) {
          toast.warning(
            `Checkout validé mais le paiement km supplémentaires a échoué : ${data.extraCharge.reason}`
          );
        } else {
          toast.success('Validation effectuée avec succès');
        }
        onReservationUpdated?.();
      }
    } catch (error) {
      console.error('Erreur checkout :', error.response?.data || error.message);
      toast.error('Erreur lors de la validation');
    }
  };

  const handleRetryExtraCharge = async () => {
    setRetryLoading(true);
    try {
      const { data } = await axios.post(
        '/checkout/' + reservation.reservationId + '/retry-extra-charge'
      );
      if (data.charged) {
        setLastExtraCharge(data);
        toast.success(`${data.amount.toFixed(2)} € prélevés avec succès`);
        onReservationUpdated?.();
      } else if (data.requiresAction && data.paymentUrl) {
        setPaymentLink({ url: data.paymentUrl, amount: data.amount, kmExcess: data.kmExcess });
        toast.warning('Aucune carte enregistrée — un lien de paiement a été généré.');
      } else {
        toast.error(`Relance échouée : ${data.status}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la relance');
    } finally {
      setRetryLoading(false);
    }
  };

  return (
    <div className="card shadow-sm p-3">
      {/* HEADER */}
      <div className="d-flex align-items-start mb-3 gap-3">
        <img
          src={reservation?.Vehicle?.principalPhotos}
          alt="vehicle"
          style={{ width: 110, borderRadius: 10, flexShrink: 0 }}
        />
        <div className="flex-grow-1">
          <h4 className="mb-1">
            {reservation?.Vehicle?.VehicleModel.marque} {reservation?.Vehicle?.VehicleModel.modele}
          </h4>
          <small className="text-muted">
            Conducteur : {reservation?.Invite?.firstName} {reservation?.Invite?.lastName}
          </small>
          <div className="text-muted small">
            {moment(reservation.startDate).format('DD MMM HH:mm')} →{' '}
            {moment(reservation.endDate).format('DD MMM HH:mm')}
          </div>
        </div>
        <button
          className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
          onClick={printInvoice}
          title="Générer la facture"
        >
          <i className="bi bi-file-earmark-text"></i>
          <span className="d-none d-sm-inline">Facture</span>
        </button>
      </div>

      {/* STATUS BADGE */}
      <div className="mb-3 d-flex flex-wrap gap-2">
        {reservation.status === 'checkin_pending_validation' && (
          <span className="badge bg-warning">Check-in en attente</span>
        )}
        {reservation.status === 'in_progress' && (
          <>
            <span className="badge bg-info">Location en cours</span>
            <span className="badge bg-primary">Retour en attente</span>
          </>
        )}
        {reservation.status === 'completed_checkout' && (
          <span className="badge bg-secondary">Checkout soumis</span>
        )}
        {reservation.status === 'completed' && (
          <span className="badge bg-success">Location terminée</span>
        )}
        {reservation.status === 'dispute_open' && (
          <span className="badge bg-danger">Litige ouvert</span>
        )}
        {extraAlreadyPaid && <span className="badge bg-success">Km extra encaissés</span>}
        {extraFailed && <span className="badge bg-danger">Paiement km extra échoué</span>}
      </div>

      {/* CHECK-IN */}
      {checkinData?.kmStart !== undefined && (
        <div className="border rounded p-3 mb-3">
          <h5>Départ</h5>
          <p className="mb-1">
            Km départ : <strong>{checkinData.kmStart}</strong>
          </p>
          <p className="mb-2">
            Carburant : <strong>{checkinData.fuelStart}</strong>
          </p>

          <button
            className="btn btn-outline-primary btn-sm me-2"
            onClick={() => setShowPhotos(!showPhotos)}
          >
            Voir photos départ
          </button>
          {showPhotos && <PhotosGallery photos={checkinData.photos} />}

          {reservation.status === 'checkin_pending_validation' && (
            <button className="btn btn-outline-primary btn-sm me-2" onClick={handleValidateCheckin}>
              Valider le check-in
            </button>
          )}
        </div>
      )}

      {/* CHECKOUT */}
      {checkoutData?.kmEnd !== undefined && (
        <div className="border rounded p-3 mb-3">
          <h5>Retour</h5>

          <p className="mb-1">
            Km retour : <strong>{checkoutData.kmEnd ?? '—'}</strong>
          </p>

          {(() => {
            const dist = parseFloat(checkoutData.kmEnd) - parseFloat(checkinData.kmStart);
            return (
              <p className="mb-1">
                Distance parcourue :{' '}
                {!isNaN(dist) && dist >= 0 ? (
                  <strong>{dist.toLocaleString('fr-FR')} km</strong>
                ) : (
                  <span className="text-muted">Non disponible</span>
                )}
              </p>
            );
          })()}

          <p className="mb-2">
            Carburant : <strong>{checkoutData.fuelEnd ?? '—'}</strong>
          </p>

          {checkoutData.comment && (
            <p className="text-muted fst-italic">"{checkoutData.comment}"</p>
          )}

          {/* Bilan kilométrique */}
          {extraPreview && (
            <div
              className={`rounded-3 p-3 mt-3 mb-2 ${
                extraPreview.hasExtra
                  ? 'bg-warning-subtle border border-warning'
                  : 'bg-success-subtle border border-success'
              }`}
            >
              <p className="fw-semibold mb-2" style={{ fontSize: 13 }}>
                Bilan kilométrique
              </p>
              <div style={{ fontSize: 12 }} className="text-muted">
                <div className="d-flex justify-content-between">
                  <span>Km départ</span>
                  <strong>{extraPreview.kmStart?.toLocaleString('fr-FR')} km</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Km retour</span>
                  <strong>{extraPreview.kmEnd?.toLocaleString('fr-FR')} km</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Distance parcourue</span>
                  <strong>{extraPreview.distanceParcourue?.toLocaleString('fr-FR')} km</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Distance incluse</span>
                  <strong>{extraPreview.freeKm?.toLocaleString('fr-FR')} km</strong>
                </div>
                {extraPreview.hasExtra && (
                  <>
                    <hr className="my-1" />
                    <div className="d-flex justify-content-between text-danger fw-bold">
                      <span>Km supplémentaires</span>
                      <span>{extraPreview.kmExcess?.toLocaleString('fr-FR')} km</span>
                    </div>
                    <div className="d-flex justify-content-between text-danger fw-bold">
                      <span>Surcharge ({extraPreview.pricePerKm} €/km)</span>
                      <span>{extraPreview.extraAmount?.toFixed(2)} €</span>
                    </div>
                  </>
                )}
                {!extraPreview.hasExtra && (
                  <div className="text-success fw-semibold mt-1">Aucun km supplémentaire</div>
                )}
              </div>
            </div>
          )}

          {/* Résultat paiement extra */}
          {lastExtraCharge && (
            <div className="alert alert-info py-2 mb-2" style={{ fontSize: 12 }}>
              <strong>{lastExtraCharge.amount.toFixed(2)} €</strong> prélevés pour{' '}
              {lastExtraCharge.kmExcess} km supplémentaires.
            </div>
          )}

          {/* Alerte paiement extra échoué + bouton retry */}
          {extraFailed && !lastExtraCharge && !paymentLink && (
            <div
              className="alert alert-danger py-2 mb-2 d-flex align-items-center justify-content-between"
              style={{ fontSize: 12 }}
            >
              <span>Paiement km supplémentaires échoué.</span>
              <button
                className="btn btn-danger btn-sm ms-3"
                onClick={handleRetryExtraCharge}
                disabled={retryLoading}
              >
                {retryLoading ? 'Relance…' : 'Relancer le paiement'}
              </button>
            </div>
          )}

          {/* Lien de paiement généré (carte non enregistrée) */}
          {paymentLink && (
            <div className="alert alert-warning py-2 mb-2" style={{ fontSize: 12 }}>
              <p className="mb-1 fw-semibold">
                Aucune carte enregistrée — lien de paiement à envoyer au conducteur
              </p>
              <p className="mb-2 text-muted">
                {paymentLink.kmExcess} km × {reservation.prixParKmSupp || 0.13} € ={' '}
                <strong>{paymentLink.amount?.toFixed(2)} €</strong>
              </p>
              <a
                href={paymentLink.url}
                target="_blank"
                rel="noreferrer"
                className="btn btn-warning btn-sm me-2"
              >
                Ouvrir le lien de paiement
              </a>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  navigator.clipboard.writeText(paymentLink.url);
                  toast.info('Lien copié');
                }}
              >
                Copier le lien
              </button>
            </div>
          )}

          <div className="d-flex gap-2 mt-2">
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => setShowCompare(!showCompare)}
            >
              Comparer photos
            </button>
            <button className="btn btn-danger btn-sm">Signaler dommage</button>
            {reservation.status !== 'completed' && (
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => setShowCheckoutConfirm(true)}
              >
                Valider le checkout
              </button>
            )}
          </div>

          {/* Comparateur photos */}
          {showCompare && (
            <div className="row mt-3">
              <div className="col">
                <p className="small text-muted">Avant</p>
                {checkinData.photos?.map((p, i) => (
                  <img
                    key={i}
                    src={p}
                    alt=""
                    style={{ width: '100%', marginBottom: 6, borderRadius: 6 }}
                  />
                ))}
              </div>
              <div className="col">
                <p className="small text-muted">Après</p>
                {checkoutData.photos?.map((p, i) => (
                  <img
                    key={i}
                    src={p}
                    alt=""
                    style={{ width: '100%', marginBottom: 6, borderRadius: 6 }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal confirmation checkout */}
      {showCheckoutConfirm && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header">
                <h5 className="modal-title fw-semibold">Confirmer la validation</h5>
                <button className="btn-close" onClick={() => setShowCheckoutConfirm(false)} />
              </div>
              <div className="modal-body">
                {extraPreview?.hasExtra ? (
                  <div className="alert alert-warning mb-3">
                    <strong>{extraPreview.kmExcess} km supplémentaires</strong> seront facturés au
                    conducteur.
                    <br />
                    <span className="fw-bold text-danger">
                      {extraPreview.extraAmount?.toFixed(2)} € prélevés automatiquement.
                    </span>
                  </div>
                ) : (
                  <p className="text-muted">
                    Aucun km supplémentaire détecté. La location sera clôturée sans surcharge.
                  </p>
                )}
                <p className="mb-0 small text-muted">Cette action est irréversible.</p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowCheckoutConfirm(false)}
                >
                  Annuler
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleValidateCheckout}>
                  Confirmer et valider
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FIN LOCATION */}
      {reservation.status === 'completed' && (
        <div className="alert alert-success">
          Location terminée et clôturée
          {extraAlreadyPaid && (
            <span className="ms-2 badge bg-success">
              + {reservation.distanceParcourueKm - reservation.distanceMaxKm} km facturés
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default ResaAdminOwner;
